import React, { useState, useEffect } from 'react';
import _get from 'lodash/get';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router';
import makeStyles from '@material-ui/core/styles/makeStyles';
import useTerminology from '@sift/skipper/helpers/useTerminology';
import * as Types from 'pages/Pages/typings';
import { LayoutObject } from 'pages/AttributesEdit/AttributesEdit';
import getPageType from 'helpers/getPageType';
import { HOME_URL, DYNAMIC_PAGES_URL } from 'components/Router/consts';
import { INDEPENDENT_PAGE_TYPE, ATTRIBUTE_PAGE_TYPE, OPEN_ROLE_PAGE_TYPE } from 'helpers/consts';

import Page from 'components/Page/newComponents';
import Breadcrumbs from 'components/Page/Breadcrumbs';
import PageTitle from 'components/Page/PageTitle';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import Tooltip from '@material-ui/core/Tooltip';
import Switch from '@material-ui/core/Switch';
import PagesTab from './tabs/PagesTab';
import SettingsTab from './tabs/SettingsTab/SettingsTab';
import Attributes from 'pages/Attributes';
import MembershipTab from './tabs/MembershipTab';
import ProfileLayoutPage from 'pages/ProfileLayout';
import PublishEntityTypeModal from './components/PublishEntityTypeModal';

const tabTypes = {
  PAGES: 'pages',
  SETTINGS: 'settings',
  ATTRIBUTE: 'attributes',
  MEMBERSHIP: 'membership',
  LAYOUT: 'layout',
};

interface Props {
  entityType: Types.EntityType;
  migrationInProgress: boolean;
  status: number;
  entityTypeConfig: any;
  onReload: () => void;
  onUpdateEntityType: (data: Types.UpdateEntity) => void;
  onUpdateField: (fieldId: string, editedAttribute: Types.UpdateField) => void;
  onUpdateLayout: (payload: LayoutObject[]) => void;
}

function PageTypes({
  entityType,
  migrationInProgress,
  status,
  entityTypeConfig,
  onReload,
  onUpdateEntityType,
  onUpdateField,
  onUpdateLayout,
}: Props) {
  let { entityTypeKey, tab } = useParams<{ entityTypeKey: string; tab?: string }>();
  const history = useHistory();
  const classes = useStyles();
  const terminology = useTerminology();
  const pageType = getPageType(entityType);
  const pageTypeName = _get(entityType, 'name');
  const [isActive, setIsActive] = useState(_get(entityType, 'isActive', false));
  const tooltipTitle = isActive
    ? `${pageTypeName} Pages are currently visible to all ${terminology.plural}. If unpublished, they will only be visible to assigned editors.`
    : `${pageTypeName} Pages are currently only visible to assigned editors. When published, they will be available to all ${terminology.plural}.`;
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const typeKey = _get(entityType, 'typeKey');

  useEffect(() => {
    document.title = pageTypeName;
  }, []);

  const handleSwitchActive = () => {
    setIsActive(!isActive);
  };

  const handleTabChange = (value: string) => {
    history.push(`${HOME_URL}${DYNAMIC_PAGES_URL}/${entityTypeKey}/${value}`);
  };

  const handlePublishSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsActive(e.target.checked);
    setIsPublishModalOpen(true);
  };

  const tabContentByType = {
    [tabTypes.ATTRIBUTE]: <Attributes typeKey={entityTypeKey} />,
    [tabTypes.LAYOUT]: <ProfileLayoutPage typeKey={typeKey} />,
    [tabTypes.MEMBERSHIP]: (
      <MembershipTab
        entityType={entityType}
        status={status}
        onUpdateEntityType={onUpdateEntityType}
        onUpdateField={onUpdateField}
        onUpdateLayout={onUpdateLayout}
      />
    ),
    [tabTypes.SETTINGS]: (
      <SettingsTab
        entityType={entityType}
        isIndependentPage={pageType === INDEPENDENT_PAGE_TYPE}
        onUpdateEntityType={onUpdateEntityType}
        config={entityTypeConfig}
        onUpdateLayout={onUpdateLayout}
      />
    ),
    [tabTypes.PAGES]: (
      <PagesTab
        entityType={entityType}
        migrationInProgress={migrationInProgress}
        onReload={onReload}
      />
    ),
  };

  const tabValue = tab && tabContentByType[tab] ? tab : tabTypes.PAGES;
  const tabContent = tabContentByType[tabValue || tabTypes.PAGES];

  return (
    <>
      <Page classes={{ root: classes.layoutRoot, container: classes.layoutContainer }}>
        <Breadcrumbs routes={['Home', 'Pages']} />
        <PageTitle
          title={pageTypeName}
          subtitle="Manage the look and feel of this page type, along with its permissions and behavior."
          actions={
            pageType !== OPEN_ROLE_PAGE_TYPE && (
              <div className={classes.flexCenter}>
                <Typography className={classes.marginRight} variant="body1">
                  Publish Pages
                </Typography>
                <Tooltip arrow id="tooltip-id" placement="top" title={tooltipTitle}>
                  <InfoIcon className={classes.icon} />
                </Tooltip>
                <Switch
                  id="publish-pages-switch"
                  inputProps={{ 'aria-label': 'publish switch' }}
                  checked={isActive}
                  onChange={handlePublishSwitchChange}
                />
              </div>
            )
          }
        />
        <Tabs onChange={(e: any, value: string) => handleTabChange(value)} value={tabValue}>
          <Tab label="All Pages" value={tabTypes.PAGES} />
          <Tab label="Attributes" value={tabTypes.ATTRIBUTE} />
          <Tab label="Layout" value={tabTypes.LAYOUT} />
          {pageType === ATTRIBUTE_PAGE_TYPE && (
            <Tab label="Membership" value={tabTypes.MEMBERSHIP} />
          )}
          {pageType !== OPEN_ROLE_PAGE_TYPE && (
            <Tab label="General Settings" value={tabTypes.SETTINGS} />
          )}
        </Tabs>
        <div className={classes.root}>{tabContent}</div>
      </Page>
      <PublishEntityTypeModal
        entityTypeKey={entityTypeKey}
        isActive={isActive}
        open={isPublishModalOpen}
        name={pageTypeName}
        onSwitchIsActive={handleSwitchActive}
        onSetOpen={setIsPublishModalOpen}
      />
    </>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    maxWidth: 1200,
  },
  layoutRoot: {
    display: 'flex',
    flex: '1 1 auto',
  },
  layoutContainer: {
    maxHeight: 'calc(100vh - 400px)',
  },
  marginRight: {
    marginRight: theme.spacing(1.5),
  },
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    color: theme.colors.gray1,
  },
}));

export default PageTypes;
